// The page behind a shared artifact link, /artifact/:shareId (Adam, 13 Sep
// 2026). No login: whoever holds the link sees the file's name, can download
// it, and for the kinds a browser can show gets a preview. Office files go
// through Microsoft's viewer, which works here precisely because the download
// needs no login; the same viewer fails inside the app for the same reason.
import React, { useEffect, useRef, useState } from 'react'
import { AgentMarkdown, ArtifactCsvPreview } from '../agent'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import useRouter from '../hooks/useRouter'
import useSharedArtifact, { formatFileSize, sharedArtifactPreviewKind } from '../hooks/useSharedArtifact'
import useStoryboardDiscussion from '../hooks/useStoryboardDiscussion'
import PageAgentDrawer from '../agent/components/PageAgentDrawer'
import DiscussStoryboardFab from '../components/assistant/DiscussStoryboardFab'
import DownloadPdfButton from '../components/dashboard/DownloadPdfButton'

const TEXT_LIMIT = 200_000

/** The text of a shared text-like artifact, fetched from the open download route. */
function useSharedText(url: string | null): { text: string | null; error: string | null } {
  const [state, setState] = useState<{ url: string | null; text: string | null; error: string | null }>({ url: null, text: null, error: null })
  useEffect(() => {
    if (!url) return
    let cancelled = false
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.text()
      })
      .then(text => { if (!cancelled) setState({ url, text: text.slice(0, TEXT_LIMIT), error: null }) })
      .catch(err => { if (!cancelled) setState({ url, text: null, error: err instanceof Error ? err.message : 'Failed to load' }) })
    return () => { cancelled = true }
  }, [url])
  if (state.url !== url) return { text: null, error: null }
  return { text: state.text, error: state.error }
}

/** JSON is pretty-printed; anything else is shown as it is. */
function formatSharedText(text: string, fileName: string): string {
  if (!fileName.toLowerCase().endsWith('.json')) return text
  try { return JSON.stringify(JSON.parse(text), null, 2) } catch { return text }
}

function absoluteUrl(path: string): string {
  if (typeof window === 'undefined') return path
  return new URL(path, window.location.origin).toString()
}

export default function SharedArtifactView() {
  const router = useRouter()
  const shareId = (router.params?.shareId as string | undefined) || undefined
  const state = useSharedArtifact(shareId)
  const loaded = state.status === 'loaded' ? state.artifact : null
  const loadedKind = loaded ? sharedArtifactPreviewKind(loaded.mime_type, loaded.file_name, loaded.artifact_type) : 'none'
  const textUrl = loaded && (loadedKind === 'markdown' || loadedKind === 'csv' || loadedKind === 'text') ? absoluteUrl(loaded.download_url) : null
  const shared = useSharedText(textUrl)
  // A framed storyboard can be talked through with the page helper (Adam,
  // 16 Sep; the agent since Douglas plan T17): the hook reads the page from
  // the same app route the frame loads from.
  // Only when the sharer switched it on, and the hook itself only offers it
  // to a signed-in Manager: someone holding the link alone never sees it.
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const discussion = useStoryboardDiscussion({
    appUrl: loaded && loadedKind === 'webapp' && loaded.app_url && loaded.discuss_enabled ? absoluteUrl(loaded.app_url) : null,
    iframeRef,
  })
  const [helperOpen, setHelperOpen] = useState(false)

  if (state.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress color="info" />
      </Box>
    )
  }
  if (state.status === 'not_found') {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 10, px: 3, textAlign: 'center' }} data-testid="shared-artifact-missing">
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>This link has nothing behind it</Typography>
        <Typography color="text.secondary">
          The file is no longer shared, or the link is not quite right. Ask whoever sent it for a fresh one.
        </Typography>
      </Box>
    )
  }
  if (state.status === 'error') {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 10, px: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Could not load this file</Typography>
        <Typography color="text.secondary">{state.message}</Typography>
      </Box>
    )
  }

  const a = state.artifact
  const downloadHref = absoluteUrl(a.download_url)
  const kind = sharedArtifactPreviewKind(a.mime_type, a.file_name, a.artifact_type)
  const size = formatFileSize(a.file_size)

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }} data-testid="shared-artifact">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {a.site_name && (
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
              Shared by {a.site_name}
            </Typography>
          )}
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' }}>
            {a.title}
          </Typography>
          {a.description && (
            <Typography sx={{ mt: 1, color: 'text.secondary', maxWidth: 720 }}>{a.description}</Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            {kind === 'webapp' && a.is_dir ? 'Web app' : a.file_name}{size ? ` · ${size}` : ''}
          </Typography>
        </Box>
        {/* A web app gets a PDF, not a zip (#1578 item 16): a zip of it
            cannot run from a folder on disk. */}
        {kind === 'webapp' && a.app_url ? (
          <DownloadPdfButton frameRef={iframeRef} variant="contained" sx={{ flexShrink: 0 }} />
        ) : (
          <Button
            variant="contained"
            color="info"
            startIcon={<DownloadIcon />}
            href={downloadHref}
            download={a.file_name}
            data-testid="shared-artifact-download"
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Download
          </Button>
        )}
      </Box>

      {kind === 'office' && (
        <Box
          component="iframe"
          title={a.title}
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(downloadHref)}`}
          sx={{ width: '100%', aspectRatio: '16 / 9', border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}
          data-testid="shared-artifact-office-preview"
        />
      )}
      {kind === 'pdf' && (
        <Box
          component="iframe"
          title={a.title}
          src={downloadHref}
          sx={{ width: '100%', height: '80vh', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        />
      )}
      {kind === 'webapp' && a.app_url && (
        // A shared web app runs in a frame fed from the link's own /app/
        // route, so its relative scripts, styles and data resolve (Adam, 14 Sep).
        <Box
          component="iframe"
          ref={iframeRef}
          title={a.title}
          src={absoluteUrl(a.app_url)}
          data-testid="shared-artifact-webapp"
          sx={{ width: '100%', height: '82vh', border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}
        />
      )}
      {kind === 'video' && (
        // The agent's narrated MP4 (Adam, 14 Sep): the download route is open,
        // so the player reads it straight from the link.
        <Box
          component="video"
          src={downloadHref}
          controls
          preload="metadata"
          data-testid="shared-artifact-video"
          sx={{ width: '100%', maxHeight: '80vh', borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: '#000', display: 'block' }}
        />
      )}
      {(kind === 'markdown' || kind === 'csv' || kind === 'text') && (
        <Box data-testid="shared-artifact-text" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: kind === 'csv' ? 1 : 3, bgcolor: 'background.paper', overflow: 'auto' }}>
          {shared.error && <Typography color="text.secondary">Could not load the file: {shared.error}</Typography>}
          {shared.text === null && !shared.error && <CircularProgress color="info" size={20} />}
          {shared.text !== null && kind === 'markdown' && <AgentMarkdown content={shared.text} />}
          {shared.text !== null && kind === 'csv' && <ArtifactCsvPreview csvContent={shared.text} maxRows={200} />}
          {shared.text !== null && kind === 'text' && (
            <Box component="pre" sx={{ m: 0, fontSize: 13, lineHeight: 1.5, fontFamily: '"Fira Code", Consolas, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {formatSharedText(shared.text, a.file_name)}
            </Box>
          )}
        </Box>
      )}
      {kind === 'image' && (
        <Box
          component="img"
          alt={a.title}
          src={downloadHref}
          sx={{ maxWidth: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        />
      )}
      {kind === 'webapp' && (
        <>
          <DiscussStoryboardFab
            visible={discussion.canDiscuss && !!a.customer}
            customer={a.customer ?? ''}
            open={helperOpen}
            onDiscuss={() => setHelperOpen(true)}
          />
          {discussion.canDiscuss && a.customer && (
            <PageAgentDrawer
              pageKey="storyboard"
              customer={a.customer}
              job={a.job ?? ''}
              open={helperOpen}
              onClose={() => setHelperOpen(false)}
            />
          )}
        </>
      )}
    </Box>
  )
}
